import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as tasksApi from '../../api/tasks';
import * as projectsApi from '../../api/projects';
import * as employeesApi from '../../api/employees';
import { formatDate, formatDateTime } from '../../utils/format';

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed', 'Closed'].map((v) => ({ value: v, label: v }));

const emptyForm = { title: '', description: '', projectId: '', assignedTo: '', dueDate: '', priority: 'Medium', status: 'Pending' };

export default function Tasks() {
  const { actor } = useAuth();
  const canCreate = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [projectOptions, setProjectOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [commentText, setCommentText] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    tasksApi
      .listTasks({ status: statusFilter || undefined, limit: 200 })
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load tasks.'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = async () => {
    setForm(emptyForm);
    if (projectOptions.length === 0) {
      const res = await projectsApi.listProjects({ limit: 500 });
      setProjectOptions(res.data.map((p) => ({ value: p.id, label: p.projectName })));
    }
    if (employeeOptions.length === 0) {
      const res = await employeesApi.listEmployees({ limit: 500 });
      setEmployeeOptions(res.data.map((e) => ({ value: e.id, label: e.name })));
    }
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await tasksApi.createTask(form);
      setSuccess('Task created.');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create task.');
    }
  };

  const openDetail = async (row) => {
    const res = await tasksApi.getTask(row.id);
    setSelectedTask(res.data);
  };

  const handleStatusUpdate = async (status) => {
    try {
      await tasksApi.updateTask(selectedTask.id, { status });
      const res = await tasksApi.getTask(selectedTask.id);
      setSelectedTask(res.data);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update task.');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await tasksApi.addTaskComment(selectedTask.id, commentText.trim());
      setCommentText('');
      const res = await tasksApi.getTask(selectedTask.id);
      setSelectedTask(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add comment.');
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await tasksApi.addTaskAttachment(selectedTask.id, file);
      const res = await tasksApi.getTask(selectedTask.id);
      setSelectedTask(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload attachment.');
    }
  };

  const columns = [
    { key: 'title', label: 'Task', render: (r) => <button type="button" className="btn-link" onClick={() => openDetail(r)}>{r.title}</button> },
    { key: 'project', label: 'Project', render: (r) => r.project?.projectName || '-' },
    { key: 'assignee', label: 'Assigned To', render: (r) => r.assignee?.name || 'Unassigned' },
    { key: 'priority', label: 'Priority', render: (r) => <StatusBadge status={r.priority} /> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'dueDate', label: 'Due Date', render: (r) => formatDate(r.dueDate) },
  ];

  return (
    <DashboardLayout title="Tasks">
      <div className="page-header">
        <div>
          <h1 className="heading">Tasks</h1>
          <p className="page-subtext">{actor.role === 'employee' ? 'Tasks assigned to you.' : 'All tasks across active projects.'}</p>
        </div>
        {canCreate && <button type="button" className="btn" onClick={openCreate}>New Task</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? <Loader text="Loading tasks..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No tasks found." />}

      {showForm && (
        <Modal
          title="New Task"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="task-form" className="btn">Create Task</button>
            </>
          }
        >
          <form id="task-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              <FormField label="Description" type="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <FormField label="Project" type="select" options={projectOptions} value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} required />
              <FormField label="Assign To" type="select" options={employeeOptions} value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} />
              <FormField label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
              <FormField label="Priority" type="select" options={PRIORITY_OPTIONS} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {selectedTask && (
        <Modal title={selectedTask.title} onClose={() => setSelectedTask(null)} width={720}>
          <p className="text-muted">{selectedTask.project?.projectName} &middot; {selectedTask.project?.client?.clientName}</p>
          <p>{selectedTask.description || 'No description.'}</p>

          <div className="btn-group" style={{ marginBottom: 16 }}>
            {STATUS_OPTIONS.map((s) => (
              <button key={s.value} type="button" className={s.value === selectedTask.status ? 'btn btn-small' : 'btn btn-secondary btn-small'} onClick={() => handleStatusUpdate(s.value)}>
                {s.label}
              </button>
            ))}
          </div>

          <h3 className="subheading">Attachments</h3>
          {selectedTask.attachments && selectedTask.attachments.length > 0 ? (
            <ul>
              {selectedTask.attachments.map((a) => <li key={a.id}>{a.fileName}</li>)}
            </ul>
          ) : (
            <p className="text-muted">No attachments yet.</p>
          )}
          <input type="file" onChange={handleAttachmentUpload} style={{ marginBottom: 16 }} />

          <h3 className="subheading">Comments</h3>
          <div className="comment-list">
            {(selectedTask.comments || []).map((c) => (
              <div key={c.id} className="comment-item">
                <div className="comment-meta">{c.author?.name} &middot; {formatDateTime(c.createdAt)}</div>
                <div>{c.comment}</div>
              </div>
            ))}
            {(!selectedTask.comments || selectedTask.comments.length === 0) && <p className="text-muted">No comments yet.</p>}
          </div>
          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 10 }}>
            <input type="text" placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            <button type="submit" className="btn btn-small">Post</button>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
